import { CompanySelector } from '@/components/global/selectors/CompanySelector';
import { LeadSelect } from '@/components/global/selectors/LeadSelect';
import InputError from '@/components/layout/InputError';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { DatePicker } from '@/components/ui/shadcn/date-picker';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import { settingLabel } from '@/lib/status-labels';
import { cn } from '@/lib/utils';
import { useForm } from '@inertiajs/react';
import { Building2, CalendarClock, SlidersHorizontal } from 'lucide-react';
import { useMemo, useState } from 'react';
import { FileDropzone } from '../components/FileDropzone';
import type { CalculationRequest, CalculationRequestOptions } from '../types';

interface CalculationRequestFormProps {
    /**
     * Only the two lists the form actually reads, so callers that are not the create
     * page — the sheet on a lead — need not assemble the whole option payload.
     */
    options: Pick<CalculationRequestOptions, 'phases' | 'leads'>;
    /** When present, the spec is pre-filled from an existing request (duplicate flow). */
    duplicateOf?: CalculationRequest | null;
    /**
     * Set when the form was opened from a lead page. The lead is then fixed — that
     * entry point exists precisely so the calculation lands on that lead.
     */
    presetLeadId?: string | null;
    onCancel?: () => void;
}

type FormValues = {
    // No company_id: the server takes the customer from the lead, so the form would
    // only be able to contradict it.
    lead_id: string;
    project_phase: string;
    object_name: string;
    system: string;
    color_exterior: string;
    color_interior: string;
    glass_g_value: string;
    glass_light_transmittance: string;
    construction_u_value: string;
    infills_enamel: string;
    sound_insulation: string;
    water_tightness_class: string;
    wind_load_resistance: string;
    air_permeability_class: string;
    notes: string;
    requested_calculation_date: string;
    save_as_draft: boolean;
    attachments: File[];
};

type SpecField = { key: keyof FormValues; labelKey: string; fallback: string; placeholderKey?: string };

/**
 * The spec block, grouped the way the paper form reads it rather than as one long
 * list: colours together, glazing together, performance classes together. Each group
 * keeps a consistent column count so the fields line up down the card.
 */
const SPEC_GROUPS: Array<{ titleKey: string; fallback: string; rows: SpecField[][] }> = [
    {
        titleKey: 'system_and_colours',
        fallback: 'System and colours',
        rows: [
            [{ key: 'system', labelKey: 'system', fallback: 'System', placeholderKey: 'system_placeholder' }],
            [
                { key: 'color_exterior', labelKey: 'color_exterior', fallback: 'Colour — exterior', placeholderKey: 'color_placeholder' },
                { key: 'color_interior', labelKey: 'color_interior', fallback: 'Colour — interior', placeholderKey: 'color_placeholder' },
            ],
        ],
    },
    {
        titleKey: 'glazing',
        fallback: 'Glazing',
        rows: [
            [
                { key: 'glass_g_value', labelKey: 'glass_g_value', fallback: 'Solar control (g)', placeholderKey: 'glass_g_value_placeholder' },
                {
                    key: 'glass_light_transmittance',
                    labelKey: 'glass_light_transmittance',
                    fallback: 'Light transmittance (Lt)',
                    placeholderKey: 'glass_light_transmittance_placeholder',
                },
            ],
            [{ key: 'infills_enamel', labelKey: 'infills_enamel', fallback: 'Infills / enamelled glass' }],
        ],
    },
    {
        titleKey: 'performance',
        fallback: 'Performance',
        rows: [
            [
                {
                    key: 'construction_u_value',
                    labelKey: 'construction_u_value',
                    fallback: 'Construction U-value',
                    placeholderKey: 'construction_u_value_placeholder',
                },
                {
                    key: 'sound_insulation',
                    labelKey: 'sound_insulation',
                    fallback: 'Sound insulation',
                    placeholderKey: 'sound_insulation_placeholder',
                },
            ],
            [
                {
                    key: 'water_tightness_class',
                    labelKey: 'water_tightness_class',
                    fallback: 'Watertightness class',
                    placeholderKey: 'water_tightness_class_placeholder',
                },
                {
                    key: 'wind_load_resistance',
                    labelKey: 'wind_load_resistance',
                    fallback: 'Wind load resistance',
                    placeholderKey: 'wind_load_resistance_placeholder',
                },
                {
                    key: 'air_permeability_class',
                    labelKey: 'air_permeability_class',
                    fallback: 'Air permeability class',
                    placeholderKey: 'air_permeability_class_placeholder',
                },
            ],
        ],
    },
];

/**
 * The digitalised "Paklausimo pateikimo forma", laid out in the same order as the
 * paper original so anyone who has filled it in recognises it immediately.
 *
 * No engineer picker: a submitted request lands in the shared queue and an engineer
 * picks it up, which is what records who accepted it.
 */
export function CalculationRequestForm({ options, duplicateOf, presetLeadId = null, onCancel }: CalculationRequestFormProps) {
    const { t } = useTranslations();
    const [files, setFiles] = useState<File[]>([]);

    const leadOptions = useMemo(() => options.leads ?? [], [options.leads]);

    const initialLeadId = presetLeadId ?? duplicateOf?.lead_id ?? '';
    /**
     * The object is named on the lead, and a calculation is a calculation *of* that
     * object — so the name comes from there rather than being retyped. A duplicate
     * keeps the name it was copied from, which is the same lead's anyway.
     */
    const initialObjectName = duplicateOf?.object_name ?? leadOptions.find((lead) => lead.id === initialLeadId)?.name ?? '';

    const { data, setData, post, processing, errors, transform } = useForm<FormValues>({
        lead_id: initialLeadId,
        project_phase: duplicateOf?.project_phase ?? '',
        object_name: initialObjectName,
        system: duplicateOf?.system ?? '',
        color_exterior: duplicateOf?.color_exterior ?? '',
        color_interior: duplicateOf?.color_interior ?? '',
        glass_g_value: duplicateOf?.glass_g_value ?? '',
        glass_light_transmittance: duplicateOf?.glass_light_transmittance ?? '',
        construction_u_value: duplicateOf?.construction_u_value ?? '',
        infills_enamel: duplicateOf?.infills_enamel ?? '',
        sound_insulation: duplicateOf?.sound_insulation ?? '',
        water_tightness_class: duplicateOf?.water_tightness_class ?? '',
        wind_load_resistance: duplicateOf?.wind_load_resistance ?? '',
        air_permeability_class: duplicateOf?.air_permeability_class ?? '',
        notes: duplicateOf?.notes ?? '',
        requested_calculation_date: '',
        save_as_draft: false,
        attachments: [],
    });

    const requiredMark = <span className="text-destructive">*</span>;
    const fieldLabel = (key: string, fallback: string) => t(`calculation_requests.form.fields.${key}`, fallback);

    const selectedLead = useMemo(() => leadOptions.find((lead) => lead.id === data.lead_id) ?? null, [leadOptions, data.lead_id]);

    /** Picking a lead renames the calculation after it, the same way creating one from a lead does. */
    const selectLead = (value: string | null) => {
        const lead = leadOptions.find((option) => option.id === value) ?? null;

        setData((current) => ({ ...current, lead_id: value ?? '', object_name: lead?.name ?? current.object_name }));
    };

    const submit = (asDraft: boolean) => {
        // setData is batched, so the draft flag and the file list are merged in via
        // transform instead — that runs against the payload at request time.
        transform((payload) => ({ ...payload, save_as_draft: asDraft, attachments: files }));

        post('/calculation-requests', { forceFormData: true });
    };

    const renderSpecField = (field: SpecField) => (
        <div key={field.key} className="space-y-1.5">
            <Label htmlFor={field.key} className="text-xs font-normal">
                {fieldLabel(field.labelKey, field.fallback)}
            </Label>
            <Input
                id={field.key}
                value={data[field.key] as string}
                onChange={(event) => setData(field.key, event.target.value as never)}
                placeholder={field.placeholderKey ? fieldLabel(field.placeholderKey, '') : undefined}
            />
            <InputError message={errors[field.key]} />
        </div>
    );

    return (
        <form
            className="space-y-6 pb-6"
            onSubmit={(event) => {
                event.preventDefault();
                submit(false);
            }}
        >
            {/* 1 — Customer and object */}
            <Card className="rounded-md">
                <CardHeader className="pb-4">
                    <div className="flex items-start gap-3">
                        <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                            1
                        </span>
                        <div className="space-y-1">
                            <CardTitle className="flex items-center gap-2">
                                <Building2 className="h-4 w-4" />
                                {t('calculation_requests.form.sections.customer', 'Customer and object')}
                            </CardTitle>
                            <CardDescription>{t('calculation_requests.form.sections.customer_hint', '')}</CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/*
                     * A calculation cannot exist without a lead — it is the commercial
                     * context being priced, and it also decides the customer. It is
                     * framed as the form's anchor rather than one field among many.
                     * Locked when the form was opened from a lead.
                     */}
                    <div className="border-primary/30 bg-primary/[0.04] space-y-2 rounded-lg border p-4">
                        <Label htmlFor="lead_id" className="text-sm font-semibold">
                            {fieldLabel('lead', 'Lead')} {requiredMark}
                        </Label>
                        <p className="text-muted-foreground text-xs">
                            {fieldLabel('lead_intro', 'Start here — the lead decides the customer and what is being priced.')}
                        </p>
                        <LeadSelect
                            leads={leadOptions}
                            value={data.lead_id || null}
                            onValueChange={selectLead}
                            disabled={presetLeadId !== null}
                            requireCompany
                            labels={{
                                placeholder: fieldLabel('lead_placeholder', 'Select a lead'),
                                search: fieldLabel('lead_search', 'Search leads…'),
                                empty: fieldLabel('lead_empty', 'No leads found.'),
                                heading: fieldLabel('lead', 'Lead'),
                                unusableNote: fieldLabel('lead_without_company', 'No customer set on this lead'),
                            }}
                        />
                        <p className="text-muted-foreground text-xs">
                            {presetLeadId !== null
                                ? fieldLabel('lead_locked_hint', 'Set from the lead this calculation was started from.')
                                : fieldLabel('lead_hint', 'Every calculation belongs to a lead.')}
                        </p>
                        <InputError message={errors.lead_id} />
                    </div>

                    {/* The customer is the lead's, never picked separately — but it reads
                        as the same kind of field as everywhere else, just disabled. */}
                    <div className="space-y-2">
                        <Label>{fieldLabel('company', 'Customer')}</Label>
                        <CompanySelector
                            disabled
                            allowCreate={false}
                            allowRegistry={false}
                            allowClear={false}
                            value={selectedLead?.company_id ?? null}
                            onValueChange={() => undefined}
                            seedCompanies={
                                selectedLead?.company_id ? [{ id: selectedLead.company_id, name: selectedLead.company_name ?? '' }] : undefined
                            }
                            labels={{ placeholder: fieldLabel('company_from_lead', 'Taken from the selected lead') }}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="object_name">
                            {fieldLabel('object_name', 'Object name')} {requiredMark}
                        </Label>
                        <Input
                            id="object_name"
                            value={data.object_name}
                            onChange={(event) => setData('object_name', event.target.value)}
                            placeholder={fieldLabel('object_name_placeholder', '')}
                        />
                        <p className="text-muted-foreground text-xs">
                            {fieldLabel('object_name_hint', "Taken from the lead. Renaming the lead's object renames this too.")}
                        </p>
                        <InputError message={errors.object_name} />
                    </div>

                    {/*
                     * Phase as a button group rather than a dropdown: there are only a
                     * handful and the choice steers everything downstream, so it earns the
                     * space. No per-value icons — phases are admin-editable, so an icon map
                     * keyed on value would silently go blank for anything they add.
                     */}
                    <div className="space-y-2">
                        <Label>{fieldLabel('project_phase', 'Project phase')}</Label>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                            {options.phases.map((phase) => {
                                const isActive = data.project_phase === phase.value;

                                return (
                                    <Button
                                        key={phase.value}
                                        type="button"
                                        variant="outline"
                                        className={cn(
                                            'h-11 justify-center gap-2 rounded-sm',
                                            isActive
                                                ? 'border-primary bg-primary/15 text-primary ring-primary/50 hover:bg-primary/20 dark:bg-primary/20 ring-1'
                                                : 'hover:bg-muted/60',
                                        )}
                                        onClick={() => setData('project_phase', isActive ? '' : phase.value)}
                                    >
                                        {settingLabel(t, 'calculation_request_phases', phase.value) || phase.label}
                                    </Button>
                                );
                            })}
                        </div>
                        <InputError message={errors.project_phase} />
                    </div>
                </CardContent>
            </Card>

            {/* 2 — Technical specification, grouped */}
            <Card className="rounded-md">
                <CardHeader className="pb-4">
                    <div className="flex items-start gap-3">
                        <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                            2
                        </span>
                        <div className="space-y-1">
                            <CardTitle className="flex items-center gap-2">
                                <SlidersHorizontal className="h-4 w-4" />
                                {t('calculation_requests.form.sections.specification', 'Technical specification')}
                            </CardTitle>
                            <CardDescription>{t('calculation_requests.form.sections.specification_hint', '')}</CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-5">
                    {SPEC_GROUPS.map((group) => (
                        <div key={group.titleKey} className="space-y-3">
                            <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                                {t(`calculation_requests.form.spec_groups.${group.titleKey}`, group.fallback)}
                            </p>
                            {group.rows.map((row, index) => (
                                <div
                                    key={index}
                                    className={cn(
                                        'grid items-start gap-x-4 gap-y-3',
                                        row.length === 2 && 'sm:grid-cols-2',
                                        row.length === 3 && 'sm:grid-cols-3',
                                    )}
                                >
                                    {row.map(renderSpecField)}
                                </div>
                            ))}
                        </div>
                    ))}

                    <div className="space-y-2 border-t pt-4">
                        <Label htmlFor="notes">{fieldLabel('notes', 'Notes')}</Label>
                        <Textarea
                            id="notes"
                            rows={4}
                            value={data.notes}
                            onChange={(event) => setData('notes', event.target.value)}
                            placeholder={fieldLabel('notes_placeholder', '')}
                        />
                        <InputError message={errors.notes} />
                    </div>
                </CardContent>
            </Card>

            {/* 3 — Deadline and documents */}
            <Card className="rounded-md">
                <CardHeader className="pb-4">
                    <div className="flex items-start gap-3">
                        <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                            3
                        </span>
                        <div className="space-y-1">
                            <CardTitle className="flex items-center gap-2">
                                <CalendarClock className="h-4 w-4" />
                                {t('calculation_requests.form.sections.schedule', 'Deadline and documents')}
                            </CardTitle>
                            <CardDescription>{t('calculation_requests.form.sections.schedule_hint', '')}</CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid items-start gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="requested_calculation_date">
                                {fieldLabel('requested_calculation_date', 'Requested calculation date')} {requiredMark}
                            </Label>
                            <DatePicker
                                value={data.requested_calculation_date}
                                onValueChange={(value) => setData('requested_calculation_date', value)}
                            />
                            <InputError message={errors.requested_calculation_date} />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>{t('calculation_requests.form.sections.files', 'Drawings and documents')}</Label>
                        <p className="text-muted-foreground text-xs">{t('calculation_requests.form.sections.files_hint', '')}</p>
                        <FileDropzone files={files} onFilesChange={setFiles} />
                    </div>
                </CardContent>
            </Card>

            {/* Sticky action bar, matching the leads and projects create forms. */}
            <div className="bg-background/95 sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-2 rounded-md border p-4 backdrop-blur">
                <Button type="button" variant="outline" onClick={onCancel ?? (() => window.history.back())}>
                    {t('calculation_requests.form.actions.cancel', 'Cancel')}
                </Button>
                <div className="flex flex-wrap items-center gap-2">
                    <Button type="button" variant="outline" disabled={processing} onClick={() => submit(true)}>
                        {t('calculation_requests.form.actions.save_draft', 'Save as draft')}
                    </Button>
                    <Button type="submit" disabled={processing}>
                        {processing
                            ? t('calculation_requests.form.actions.saving', 'Saving...')
                            : t('calculation_requests.form.actions.submit', 'Submit request')}
                    </Button>
                </div>
            </div>
        </form>
    );
}
